import * as React from 'react';

import {
    HelpKeys,
    LookupDataEnums,
    NotePurposeTypesEnum,
    NoteStatusTypes,
    NoteTypes,
    UserRightsEnum,
    ZoneContextType,
} from '../../utilities/Constants';
import { INoteCategoryTypeModel, INoteModel } from '../../interfaces/INote';

import Authorization from '../../stores/Authorization';
import { DisplayMessages } from '../../utilities/DisplayMessages';
import { DocumentTypeEnum } from '../../utilities/DbConstants';
import { ILookupModel } from '../../interfaces/ILookup';
import { IUserModel } from '../../interfaces/IUser';
import { IValidation } from '../../interfaces/IError';
import { Loader } from '../../components/shared/Loader';
import { LocalRoutes } from '../../utilities/LocalRoutes';
import { MainContentOverlayComponent } from '../../components/shared/MainContentOverlay';
import { NoteEditor } from '../../components/notes/note-editor/NoteEditor';
import NoteHelper from '../../utilities/NoteHelper';
import { NoteList } from '../../components/notes/NoteList';
import ValidateUtils from '../../shared/validations';
import { cloneDeep } from 'lodash';

interface IZoneNotesProps {
    user: IUserModel;
    refreshIdentifier?: string;
    zoneContext: ZoneContextType;
}

interface IZoneNotesState {
    pendingResponse: boolean;
    validation: IValidation;
    noteCategoryTypes: INoteCategoryTypeModel[];
    allNotes: INoteModel[];
    originalNotes: INoteModel[];
    currentNote: INoteModel;
    openNoteEditor: boolean;
    zoneOrganizationGuid: string;
    helpText: ILookupModel[];
    loggedInUserDataScopeId: number;
    forceNoteEditorInReadOnlyMode?: boolean;
}

export class ZoneNotes extends React.Component<IZoneNotesProps, IZoneNotesState> {
    constructor(props: any) {
        super(props);
        this.state = {
            pendingResponse: true,
            validation: {},
            currentNote: {
                status: { id: NoteStatusTypes.Open },
                purpose: { id: NotePurposeTypesEnum.Info },
            },
            allNotes: [],
            originalNotes: [],
            noteCategoryTypes: [],
            openNoteEditor: false,
            zoneOrganizationGuid: '',
            helpText: [],
            loggedInUserDataScopeId: 0,
        };
    }

    componentDidMount() {
        if (
            !Authorization.isAuthorizedToRoute(
                LocalRoutes.ZoneNotes,
                this.props.user,
                this.props.zoneContext.zoneGuid
            )
        )
            window.location.assign(LocalRoutes.AccessDenied);
        this.loadInitialData();
        this.getHelpText();
    }

    componentDidUpdate(prevProps: IZoneNotesProps) {
        if (
            this.props.refreshIdentifier &&
            prevProps.refreshIdentifier != this.props.refreshIdentifier
        ) {
            this.setState(
                {
                    pendingResponse: true,
                    validation: {},
                    currentNote: {
                        status: { id: NoteStatusTypes.Open },
                        purpose: { id: NotePurposeTypesEnum.Info },
                    },
                    allNotes: [],
                    originalNotes: [],
                    noteCategoryTypes: [],
                    openNoteEditor: false,
                    zoneOrganizationGuid: '',
                    loggedInUserDataScopeId: 0,
                },
                this.loadInitialData
            );
        }
    }

    loadInitialData = async () => {
        if (this.props.zoneContext.zoneGuid === '') {
            return;
        }

        const noteCategoryTypes = await this.fetchNoteCategoryTypes();
        if (!noteCategoryTypes) return;

        let zoneNotes = await this.fetchNotes(this.props.zoneContext.zoneGuid);
        if (!zoneNotes) zoneNotes = [];

        const loggedInUserDataScopeId = Authorization.getUserDefaultOrganizationDataScope(
            this.props.user,
            this.props.zoneContext.zoneGuid
        );

        this.setState({
            noteCategoryTypes: noteCategoryTypes,
            allNotes: zoneNotes,
            originalNotes: JSON.parse(JSON.stringify(zoneNotes)),
            zoneOrganizationGuid: this.props.zoneContext.zoneGuid,
            pendingResponse: false,
            loggedInUserDataScopeId: loggedInUserDataScopeId,
        });
    };

    fetchNoteCategoryTypes = async () => {
        const response = await this.props.zoneContext.zoneApiClient.getNoteCategoryTypes(
            LookupDataEnums.ZoneNoteCategories
        );
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }
        return response.payload;
    };

    fetchNotes = async (zoneOrganizationGuid: string) => {
        const response = await this.props.zoneContext.zoneApiClient.getNotes(
            NoteTypes.ZoneNote,
            zoneOrganizationGuid
        );
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }
        return response.payload;
    };

    handleCancelNoteEditor = () => {
        this.setState({
            currentNote: {
                status: { id: NoteStatusTypes.Open },
                purpose: { id: NotePurposeTypesEnum.Info },
            },
            openNoteEditor: false,
        });
    };

    handleSaveNoteComplete = (noteItem: INoteModel) => {
        const allNotesCopy = cloneDeep(this.state.allNotes);
        const foundIdx = allNotesCopy.findIndex((note) => note.guid === noteItem.guid);

        const noteItemWithCleanModifiedDate = {
            ...noteItem,
            modifiedDate: noteItem.modifiedDate?.replace('Z', ''),
        };
        if (foundIdx === -1) {
            allNotesCopy.push(cloneDeep(noteItemWithCleanModifiedDate));
        } else {
            allNotesCopy.splice(foundIdx, 1, cloneDeep(noteItemWithCleanModifiedDate));
        }
        allNotesCopy.sort(NoteHelper.sortNotesByStarredAndThenByModifiedDate);

        this.setState({ allNotes: allNotesCopy, openNoteEditor: false });
    };

    handleViewEditNote = (noteGuidToEdit: string, readOnly?: boolean) => {
        const validation: IValidation = {};

        if (!this.state.allNotes) {
            validation.model = [DisplayMessages.UnexpectedError];
            this.setState({ pendingResponse: false, validation: validation });
            return;
        }

        const currentNote = this.state.allNotes.find((x) => x.guid == noteGuidToEdit);
        if (!currentNote) {
            validation.model = [DisplayMessages.UnexpectedError];
            this.setState({ pendingResponse: false, validation: validation });
            return;
        }

        this.setState({
            currentNote: JSON.parse(JSON.stringify(currentNote)),
            openNoteEditor: true,
            forceNoteEditorInReadOnlyMode: readOnly,
        });
    };

    refreshNotes = (notes: INoteModel[]) => {
        this.setState({ allNotes: notes });
    };

    handleAddNote = () => {
        const currentNote: INoteModel = {
            type: { id: NoteTypes.ZoneNote },
            status: { id: NoteStatusTypes.Open },
            organizationGuid: this.state.zoneOrganizationGuid,
            purpose: { id: NotePurposeTypesEnum.Info },
        };

        this.setState({
            openNoteEditor: true,
            currentNote: currentNote,
            forceNoteEditorInReadOnlyMode: false,
        });
    };

    getHelpText = async () => {
        this.setState({ pendingResponse: true, validation: {} });
        const response = await this.props.zoneContext.zoneApiClient.getOrganizationSettings();
        if (response.httpResponse.status == 401) {
            window.location.reload();
            return;
        }
        if (response.errorMessage) {
            this.setState({
                validation: ValidateUtils.parseErrors(response.errors, response.errorMessage),
                pendingResponse: false,
            });
            return;
        }
        if (response.payload) {
            const organizationSettings = response.payload;
            const match = organizationSettings.find(
                (x) => x.settingName == HelpKeys.ZoneNotes.Name
            );
            if (match)
                this.setState({
                    helpText: [
                        {
                            id: match.id,
                            name: 'ZoneNotes',
                            displayName: 'Zone Notes',
                            description: match.settingValue,
                        },
                    ],
                });
        }
    };

    render() {
        const userHasAtLeastOneNoteRight = Authorization.userHasAtLeastOneRight(
            this.props.user,
            [UserRightsEnum.AddZoneNote, UserRightsEnum.EditZoneNote, UserRightsEnum.ViewZoneNotes],
            this.props.zoneContext.zoneGuid
        );

        const showNoteEditor = userHasAtLeastOneNoteRight && this.state.openNoteEditor;

        if (this.state.pendingResponse) return <Loader />;

        return (
            <>
                {showNoteEditor && (
                    <MainContentOverlayComponent>
                        <NoteEditor
                            isReadonlyMode={this.state.forceNoteEditorInReadOnlyMode}
                            noteCategoryTypes={this.state.noteCategoryTypes}
                            currentNote={this.state.currentNote}
                            onSaveComplete={this.handleSaveNoteComplete}
                            onCancel={this.handleCancelNoteEditor}
                        />
                    </MainContentOverlayComponent>
                )}

                <div>
                    <span className="text-danger">{this.state.validation.model}</span>
                </div>

                <NoteList
                    printHide={showNoteEditor}
                    title="Zone Notes"
                    user={this.props.user}
                    notes={this.state.allNotes}
                    noteCategoryTypes={this.state.noteCategoryTypes}
                    handleAddNote={this.handleAddNote}
                    handleViewEditNote={this.handleViewEditNote}
                    noteType={NoteTypes.CaseNote}
                    showHorizontalPadding
                    refreshParent={this.refreshNotes}
                    zoneContext={this.props.zoneContext}
                />
            </>
        );
    }
}
